import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import styles from "./PaymentLog.module.css";

const fmt = (n) =>
  `KES ${Number(n ?? 0).toLocaleString("en-KE", { minimumFractionDigits: 0 })}`;

const EMPTY_ENTRY = { amount: "", payment_date: "", notes: "" };

export default function PaymentLog({ clientId, totalAmount, onPaymentAdded }) {
  const { profile } = useAuth();
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [form,    setForm]    = useState(EMPTY_ENTRY);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");
  const [showForm,setShowForm]= useState(false);

  useEffect(() => { if (clientId) fetchLogs(); }, [clientId]); // eslint-disable-line

  async function fetchLogs() {
    setLoading(true);
    const { data } = await supabase
      .from("payment_logs")
      .select("*, recorded_by:employees(full_name)")
      .eq("client_id", clientId)
      .order("payment_date", { ascending: false });
    setLogs(data ?? []);
    setLoading(false);
  }

  const totalReceived = logs.reduce((s, l) => s + Number(l.amount), 0);
  const balance = Number(totalAmount ?? 0) - totalReceived;

  function set(field, val) {
    setForm((f) => ({ ...f, [field]: val }));
    setErr("");
  }

  async function handleAdd() {
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0) {
      setErr("Enter a valid payment amount.");
      return;
    }
    if (!form.payment_date) {
      setErr("Select the payment date.");
      return;
    }

    setSaving(true);
    setErr("");

    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out. Try again.")), 15000)
    );

    try {
      const { error } = await Promise.race([
        supabase.from("payment_logs").insert({
          client_id:    clientId,
          amount:       Number(form.amount),
          payment_date: form.payment_date,
          notes:        form.notes || null,
          recorded_by:  profile?.id ?? null,
        }),
        timeout,
      ]);

      if (error) throw error;

      setForm(EMPTY_ENTRY);
      setShowForm(false);
      await fetchLogs();
      if (onPaymentAdded) onPaymentAdded();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteLog(id) {
    if (!window.confirm("Remove this payment entry?")) return;
    await supabase.from("payment_logs").delete().eq("id", id);
    fetchLogs();
    if (onPaymentAdded) onPaymentAdded();
  }

  return (
    <div className={styles.wrap}>
      {/* Summary */}
      <div className={styles.summary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Total Amount</span>
          <span className={styles.summaryValue}>{fmt(totalAmount)}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Total Received</span>
          <span className={`${styles.summaryValue} ${styles.green}`}>
            {fmt(totalReceived)}
          </span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Balance Due</span>
          <span className={`${styles.summaryValue} ${balance > 0 ? styles.orange : styles.green}`}>
            {fmt(balance)}
          </span>
        </div>
      </div>

      {/* Add payment form toggle */}
      {!showForm && (
        <button className={styles.addBtn} onClick={() => setShowForm(true)}>
          + Record Payment
        </button>
      )}

      {showForm && (
        <div className={styles.addForm}>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>Amount (KES) *</label>
              <input
                type="number"
                min="1"
                step="0.01"
                placeholder="e.g. 25000"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
              />
            </div>
            <div className={styles.formField}>
              <label>Payment Date *</label>
              <input
                type="date"
                value={form.payment_date}
                onChange={(e) => set("payment_date", e.target.value)}
              />
            </div>
          </div>
          <div className={styles.formField}>
            <label>Notes (optional)</label>
            <input
              type="text"
              placeholder="e.g. M-Pesa ref: QDF123"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </div>
          {err && <div className={styles.err}>{err}</div>}
          <div className={styles.formActions}>
            <button
              className={styles.cancelBtn}
              onClick={() => { setShowForm(false); setForm(EMPTY_ENTRY); setErr(""); }}
            >
              Cancel
            </button>
            <button
              className={styles.saveBtn}
              onClick={handleAdd}
              disabled={saving}
            >
              {saving ? "Saving…" : "Add Payment"}
            </button>
          </div>
        </div>
      )}

      {/* Payment history */}
      <div className={styles.logLabel}>Payment History</div>

      {loading ? (
        <div className={styles.empty}>Loading…</div>
      ) : logs.length === 0 ? (
        <div className={styles.empty}>No payments recorded yet.</div>
      ) : (
        <div className={styles.logList}>
          {logs.map((log) => (
            <div key={log.id} className={styles.logRow}>
              <div className={styles.logLeft}>
                <div className={styles.logAmount}>{fmt(log.amount)}</div>
                <div className={styles.logMeta}>
                  {log.payment_date}
                  {log.recorded_by?.full_name && (
                    <span> · by {log.recorded_by.full_name}</span>
                  )}
                  {log.notes && <span> · {log.notes}</span>}
                </div>
              </div>
              <button
                className={styles.deleteBtn}
                onClick={() => deleteLog(log.id)}
                title="Remove payment"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}